/**
 * src/pages/public/BookingPortal.jsx — the client-facing booking page.
 *
 * A linear three-step flow rather than one long form, because the middle step
 * depends on the first: slots cannot be shown until an artist and a date are
 * chosen. Steps are: pick an artist → pick a slot → enter details and pay.
 *
 * The page is branded with the studio's accent colour, since a client reaching
 * it from the studio's Instagram should not feel handed off to a third party.
 *
 * Nothing is booked here. Submitting creates a pending hold and redirects to
 * Stripe; the appointment is only confirmed when the deposit clears.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { api } from '../../lib/api.js';
import { useApi } from '../../lib/useApi.js';
import { formatMoney, formatTime } from '../../lib/format.js';
import { ErrorBanner, Empty, Loader } from '../../components/ui.jsx';
import PaymentForm from '../../components/PaymentForm.jsx';

/**
 * Today as YYYY-MM-DD, in a given zone.
 *
 * `en-CA` conveniently formats as YYYY-MM-DD, so this needs no manual
 * assembly. Falls back to the browser's own zone when none is given, for the
 * brief window before the studio's timezone is known (see BookingPortal's
 * effect that corrects the default once it loads).
 *
 * @param {string} [timeZone]
 * @returns {string}
 */
function todayIso(timeZone) {
  return new Intl.DateTimeFormat('en-CA', timeZone ? { timeZone } : undefined).format(new Date());
}

export default function BookingPortal() {
  const { slug } = useParams();
  const [params] = useSearchParams();

  const studio = useApi(`/api/public/${slug}`);
  const artists = useApi(`/api/public/${slug}/artists`);

  const [artistId, setArtistId] = useState('');
  const [date, setDate] = useState(todayIso());
  const dateTouched = useRef(false);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', description: '' });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // Set once the booking request comes back with a client secret, which means
  // the studio is on embedded payments. The redirect path never sets it.
  const [payment, setPayment] = useState(null);
  const [paid, setPaid] = useState(false);

  const availability = useApi(
    artistId ? `/api/public/${slug}/availability?artistId=${artistId}&date=${date}` : null,
    [artistId, date],
  );

  const branding = studio.data?.studio;

  useEffect(() => {
    if (branding?.accentColor) {
      document.documentElement.style.setProperty('--accent', branding.accentColor);
    }
    return () => document.documentElement.style.removeProperty('--accent');
  }, [branding?.accentColor]);

  // The initial `date` default is computed before the studio's timezone is
  // known (see todayIso's fallback), which can be wrong near midnight in
  // either zone. Correct it once the real zone arrives — unless the visitor
  // has already picked a date themselves, which this must never overwrite.
  useEffect(() => {
    if (branding?.timezone && !dateTouched.current) {
      setDate(todayIso(branding.timezone));
    }
  }, [branding?.timezone]);

  // Changing artist or date invalidates the chosen slot.
  useEffect(() => setSlot(null), [artistId, date]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(`/api/public/${slug}/booking-request`, {
        clientName: form.name,
        clientEmail: form.email,
        clientPhone: form.phone || undefined,
        artistId,
        startsAt: slot.startsAt,
        description: form.description || undefined,
      });

      // Two shapes, depending on the studio's payment setting. A client secret
      // means the card is collected here; a checkout URL means Stripe's own
      // page. Either way the appointment is already held as 'pending' and is
      // only confirmed by the webhook.
      if (res.clientSecret) {
        setPayment({ clientSecret: res.clientSecret, amount: res.amount });
        setBusy(false);
        return;
      }
      window.location.assign(res.checkoutUrl);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  // Returning from a successful Checkout, or just paid inline.
  if (params.get('appointment') || paid) {
    return (
      <div className="public-page">
        <div className="card stack center">
          <h2>You&rsquo;re booked in</h2>
          <p className="muted">
            Your deposit has gone through. Check your email for confirmation and your consent form —
            please sign it before you come in.
          </p>
        </div>
      </div>
    );
  }

  const step = !artistId ? 1 : !slot ? 2 : 3;

  return (
    <div className="public-page">
      <Loader loading={studio.loading} error={studio.error}>
        {branding ? (
          <>
            <header className="public-header">
              {branding.logoUrl ? <img src={branding.logoUrl} alt="" /> : null}
              <h1>{branding.name}</h1>
              <p className="muted small">
                Book a session. A {formatMoney(branding.defaultDepositAmount, branding.currency)}{' '}
                deposit secures your slot and comes off the price on the day.
              </p>
            </header>

            {params.get('cancelled') ? (
              <div className="alert" style={{ marginBottom: 16 }}>
                Payment was cancelled — nothing has been booked. Pick a slot to try again.
              </div>
            ) : null}
            {!branding.payoutsReady ? (
              <div className="alert" style={{ marginBottom: 16 }}>
                This studio isn&rsquo;t accepting online deposits yet — check back soon.
              </div>
            ) : null}

            <div className="steps">
              <span className={`step${step === 1 ? ' current' : step > 1 ? ' done' : ''}`}>1 · Artist</span>
              <span className={`step${step === 2 ? ' current' : step > 2 ? ' done' : ''}`}>2 · Time</span>
              <span className={`step${step === 3 ? ' current' : ''}`}>3 · Details</span>
            </div>

            <div className="stack-lg">
              <section className="card">
                <h2 style={{ marginBottom: 12 }}>Choose an artist</h2>
                <Loader loading={artists.loading} error={artists.error}>
                  {(artists.data?.artists ?? []).length === 0 ? (
                    <Empty>This studio has no artists taking bookings right now.</Empty>
                  ) : (
                    <div className="stack">
                      {artists.data.artists.map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          className={`btn${artistId === a.id ? ' btn-primary' : ''}`}
                          style={{ textAlign: 'left', padding: 12 }}
                          onClick={() => setArtistId(a.id)}
                        >
                          <span className="row">
                            {a.avatarUrl ? (
                              <img
                                src={a.avatarUrl}
                                alt=""
                                style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                              />
                            ) : null}
                            <span>
                              <strong>{a.name}</strong>
                              {a.bio ? (
                                <span className="small" style={{ display: 'block', fontWeight: 400 }}>
                                  {a.bio}
                                </span>
                              ) : null}
                              {a.instagramHandle ? (
                                <span className="small muted" style={{ display: 'block' }}>
                                  {a.instagramHandle}
                                </span>
                              ) : null}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </Loader>
              </section>

              {artistId ? (
                <section className="card">
                  <div className="row-between" style={{ marginBottom: 12 }}>
                    <h2>Pick a time</h2>
                    <input
                      type="date"
                      value={date}
                      min={todayIso(branding.timezone)}
                      onChange={(e) => {
                        dateTouched.current = true;
                        setDate(e.target.value);
                      }}
                      aria-label="Date"
                      style={{ width: 180 }}
                    />
                  </div>

                  <Loader loading={availability.loading} error={availability.error}>
                    {(availability.data?.slots ?? []).length === 0 ? (
                      <Empty>Nothing free that day. Try another date.</Empty>
                    ) : (
                      <div className="slot-grid">
                        {availability.data.slots.map((s) => (
                          <button
                            key={s.startsAt}
                            type="button"
                            className={`btn slot${slot?.startsAt === s.startsAt ? ' selected' : ''}`}
                            onClick={() => setSlot(s)}
                          >
                            {formatTime(s.startsAt, branding.timezone)}
                          </button>
                        ))}
                      </div>
                    )}
                  </Loader>
                  {availability.data?.sessionMinutes ? (
                    <p className="muted small" style={{ marginTop: 10 }}>
                      Sessions are {availability.data.sessionMinutes / 60} hours. If your piece needs
                      longer, book the first session and your artist will plan the rest with you.
                    </p>
                  ) : null}
                </section>
              ) : null}

              {slot && payment ? (
                <section className="card">
                  <h2 style={{ marginBottom: 12 }}>Payment</h2>
                  <p className="muted small" style={{ marginBottom: 14 }}>
                    Holding {formatTime(slot.startsAt, branding.timezone)} for you. Your slot is confirmed once the
                    deposit clears.
                  </p>
                  <PaymentForm
                    publishableKey={branding.stripePublishableKey}
                    clientSecret={payment.clientSecret}
                    amount={payment.amount}
                    currency={branding.currency}
                    accentColor={branding.accentColor}
                    onSuccess={() => setPaid(true)}
                  />
                </section>
              ) : slot ? (
                <section className="card">
                  <h2 style={{ marginBottom: 12 }}>Your details</h2>
                  <form className="stack" onSubmit={submit}>
                    <ErrorBanner>{error}</ErrorBanner>

                    <div>
                      <label htmlFor="bname">Name</label>
                      <input id="bname" value={form.name} onChange={set('name')} required autoComplete="name" />
                    </div>
                    <div className="row wrap">
                      <div className="grow">
                        <label htmlFor="bemail">Email</label>
                        <input
                          id="bemail"
                          type="email"
                          value={form.email}
                          onChange={set('email')}
                          required
                          autoComplete="email"
                        />
                      </div>
                      <div className="grow">
                        <label htmlFor="bphone">Phone</label>
                        <input id="bphone" value={form.phone} onChange={set('phone')} autoComplete="tel" />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="bdesc">What are you thinking of?</label>
                      <textarea
                        id="bdesc"
                        value={form.description}
                        onChange={set('description')}
                        placeholder="Subject, size, placement, any references you have."
                      />
                    </div>

                    <button type="submit" className="btn btn-primary" disabled={busy || !branding.payoutsReady}>
                      {busy
                        ? 'Redirecting…'
                        : `Pay ${formatMoney(branding.defaultDepositAmount, branding.currency)} deposit`}
                    </button>
                    <p className="muted small">
                      You&rsquo;ll be taken to Stripe to pay. Your slot is held while you check out and
                      confirmed once the deposit clears.
                    </p>
                  </form>
                </section>
              ) : null}
            </div>
          </>
        ) : null}
      </Loader>
    </div>
  );
}
